const NAMED_ENTITIES: Record<string, string> = {
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  amp: '&',
};

const MAX_CODE_POINT = 0x10ffff; // highest code point Unicode defines; fromCodePoint throws above this
const MIN_SURROGATE_CODE_POINT = 0xd800; // start of the UTF-16 surrogate range, never a real standalone character
const MAX_SURROGATE_CODE_POINT = 0xdfff; // end of the UTF-16 surrogate range, never a real standalone character

const decodeEntity = (match: string, token: string): string => {
  const lower = token.toLowerCase();

  if (lower in NAMED_ENTITIES) {
    return NAMED_ENTITIES[lower];
  }

  const codePoint = lower.startsWith('#x')
    ? parseInt(lower.slice(2), 16)
    : Number(lower.slice(1));

  if (
    !Number.isInteger(codePoint) ||
    codePoint > MAX_CODE_POINT ||
    (codePoint >= MIN_SURROGATE_CODE_POINT &&
      codePoint <= MAX_SURROGATE_CODE_POINT)
  ) {
    return match;
  }

  return String.fromCodePoint(codePoint);
};

/**
 * Decode common HTML entities in a plain-text string (after tags have been
 * stripped, if any).
 *
 * In the browser this delegates to the DOM, which correctly decodes every
 * named/numeric entity. During SSR (no `document`) it falls back to a single
 * regex pass covering named entities plus arbitrary numeric/hex entities, so
 * a decoded entity's output is never re-scanned and re-decoded (e.g.
 * `&amp;lt;` and `&#38;lt;` both become `&lt;`, not `<`).
 */
export const decodeHtmlEntities = (value: string): string => {
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;

    // Normalize the decoded non-breaking space (U+00A0) to a regular space
    // so browser-side decoding matches the SSR regex fallback below (and
    // the historical behavior every call site already relies on).
    return textarea.value.replace(/\u00A0/g, ' ');
  }

  return value
    .replace(/&(#x[0-9a-f]+|#\d+|lt|gt|quot|apos|nbsp|amp);/gi, decodeEntity)
    .replace(/\u00A0/g, ' ');
};
